import classnames from 'classnames';
import type { IconMapper, TranslationKeyMapper } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { ButtonsGroup } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { useUserType } from 'hooks/useUserType';
import { ChangeEvent, FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { FilterType, StatusFilter } from 'state/quotes/dashboard/filter/filter.model';
import type { DashboardFilter } from 'state/quotes/dashboard/model';
import type { QuoteIndexableData } from 'state/quotes/dashboard/selectors';
import type { GroupColor } from 'state/quotes/dashboard/tiles/tiles.model';
import styled from 'styled-components';
import { mediaQueryDown, mediaQueryEqualOrUpper } from 'utils/mediaQueries';

import { FilterByClient } from './Filter.ByClient';
import { FilterByPinned } from './Filter.ByPinned';
import { FilterByStatus } from './Filter.ByStatus';
import type { PinnedGroupColors } from './Filter.helper';
import { getFilterCountsMap } from './Filter.helper';

const Container = styled.div`
  ${mediaQueryEqualOrUpper('lg')`
    width: 28rem;
  `};
  ${mediaQueryDown('lg')`
    width: 100%;
  `};
`;

const StyledSearchInput = styled.input`
  min-width: 20rem !important;
`;

const availableFilterTypes: FilterType[] = ['byStatus', 'byPinned', 'byClient'];

export interface FilterProps {
  className?: string;
  selectedFilter: DashboardFilter;
  quickSearchTerm?: string;
  quotes: QuoteIndexableData[];
  pinnedGroupColors: PinnedGroupColors;
  onFilterTypeChanged(filterType: FilterType): void;
  onByStatusFilterChanged(filter: StatusFilter): void;
  onByClientFilterChanged(clientId: string, product?: string): void;
  onByPinnedFilterChanged(pinnedGroup?: string): void;
  onPinnedGroupRenamed(previousGroupName: string, newGroupName: string): void;
  onPinnedGroupColorChanged: (pinnedGroup: string, color?: GroupColor) => void;
  onTilesDeleted(quoteIds: string[]): void;
  onQuickSearchTermChanged(term?: string): void;
}

export const Filter: FC<FilterProps> = ({
  className,
  selectedFilter,
  quickSearchTerm,
  quotes,
  pinnedGroupColors,
  onFilterTypeChanged,
  onByStatusFilterChanged,
  onByClientFilterChanged,
  onByPinnedFilterChanged,
  onPinnedGroupRenamed,
  onPinnedGroupColorChanged,
  onTilesDeleted,
  onQuickSearchTermChanged,
}) => {
  const { formatMessage } = useIntl();
  const userType = useUserType();

  const filtersCounts = useMemo(() => getFilterCountsMap(quotes), [quotes]);

  const translationKey = useCallback<TranslationKeyMapper<FilterType>>(
    filter => (filter === 'byClient' && userType === 'externalClient' ? 'byProduct' : filter),
    [userType],
  );

  const icon = useCallback<IconMapper<FilterType>>(
    filter => {
      switch (filter) {
        case 'byStatus':
          return 'menu';
        case 'byPinned':
          return 'push_pin';
        case 'byClient':
          return userType === 'externalClient' ? 'category' : 'people';
        default:
          return '';
      }
    },
    [userType],
  );

  const onSearchChanged = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onQuickSearchTermChanged(e.currentTarget.value || undefined),
    [onQuickSearchTermChanged],
  );

  return (
    <Container className={classnames('bg-lvl1', className)} data-e2e="filter-panel">
      <div className="pt-4 px-3 pb-3">
        <h2 className="text-nowrap">{formatMessage({ id: 'dashboard.filterTitle' })}</h2>
        <div className="input-group input-group-lg mt-3">
          <span className="input-group-text">
            <em className="icon">search</em>
          </span>
          <StyledSearchInput
            type="search"
            className="form-control form-control-lg"
            placeholder={formatMessage({ id: 'dashboard.searchActiveQuotes' })}
            value={quickSearchTerm || ''}
            onChange={onSearchChanged}
            data-tour="quoteDashboard.search"
          />
        </div>
        <ButtonsGroup
          className="mt-2 w-100"
          items={availableFilterTypes}
          selectedItems={selectedFilter.type}
          translationPrefix="dashboard.filter"
          translationKey={translationKey}
          guidedTourStepPrefix="quoteDashboard.menu"
          onSelect={items => onFilterTypeChanged(items[0])}
          isRadio
          icon={icon}
          e2e="filter-type-radiobuttons"
        />
      </div>
      {selectedFilter.type === 'byStatus' && (
        <FilterByStatus
          selectedStatus={selectedFilter.status}
          filtersCounts={filtersCounts}
          quotes={quotes}
          onByStatusFilterChanged={onByStatusFilterChanged}
          onTilesDeleted={onTilesDeleted}
        />
      )}
      {selectedFilter.type === 'byPinned' && (
        <FilterByPinned
          selectedPinnedGroup={selectedFilter.pinnedGroup}
          filtersCounts={filtersCounts}
          quotes={quotes}
          pinnedGroupColors={pinnedGroupColors}
          onByPinnedFilterChanged={onByPinnedFilterChanged}
          onPinnedGroupRenamed={onPinnedGroupRenamed}
          onPinnedGroupColorChanged={onPinnedGroupColorChanged}
          onTilesDeleted={onTilesDeleted}
        />
      )}
      {selectedFilter.type === 'byClient' && (
        <FilterByClient
          userType={userType}
          selectedClient={selectedFilter.client}
          selectedProduct={selectedFilter.product}
          quotes={quotes}
          onByClientFilterChanged={onByClientFilterChanged}
          onTilesDeleted={onTilesDeleted}
        />
      )}
    </Container>
  );
};

Filter.displayName = 'Filter';
